import React from 'react'
import {useCallback, useEffect, useState} from 'react'
import {Button, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useTelephony} from '../../hooks/useTelephony'
import {
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TelephonyDrawerStateDetail,
} from '../../types/telephony'

const floatingToolsStyle: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)',
  zIndex: 1051,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}

const floatingTabStyle: React.CSSProperties = {
  borderRadius: '10px 0 0 10px',
  padding: '8px 12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
}

const incomingPulseStyle: React.CSSProperties = {
  backgroundColor: '#16a34a',
  borderColor: '#16a34a',
  color: '#fff',
  animation: 'policytools-vicidial-pulse 1s ease-in-out infinite',
}

const activeCallStyle: React.CSSProperties = {
  backgroundColor: '#15803d',
  borderColor: '#15803d',
  color: '#fff',
}

const hasMeaningfulVicidialValue = (value?: string) => {
  const normalized = String(value || '').trim().toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const buildCallerLabel = (firstName?: string, lastName?: string, phoneNumber?: string, vendorLeadCode?: string) => {
  const displayName = [String(firstName || '').trim(), String(lastName || '').trim()]
    .filter((value) => hasMeaningfulVicidialValue(value))
    .join(' ')

  return String(displayName || phoneNumber || vendorLeadCode || '').trim()
}

const isDispositionPendingState = (status?: string, realTimeSubStatus?: string) => {
  const statusValue = String(status || '').trim().toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '').trim().toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

export const TelephonyFloatingButton: React.FC = () => {
  const telephony = useTelephony()
  const [drawerState, setDrawerState] = useState<TelephonyDrawerStateDetail>({
    mounted: false,
    visible: false,
    provider: undefined,
  })
  const tooltipLabel =
    drawerState.mounted && drawerState.provider === 'VICIDIAL'
      ? drawerState.visible
        ? 'Hide PolicyDial Webphone'
        : 'Show PolicyDial Webphone'
      : 'Open PolicyDial Webphone'

  const vicidialStatus = String(telephony.vicidialSession.status || '').trim().toUpperCase()
  const vicidialSubStatus = String(telephony.vicidialSession.realTimeSubStatus || '').trim().toUpperCase()
  const isPausedState = vicidialStatus === 'PAUSED'
  const dispositionPending = isDispositionPendingState(vicidialStatus, vicidialSubStatus)
  const hasCallContext =
    !dispositionPending &&
    (hasMeaningfulVicidialValue(telephony.vicidialSession.callId) ||
      hasMeaningfulVicidialValue(telephony.vicidialSession.leadId))
  const hasIncomingLeadContext =
    !dispositionPending &&
    !isPausedState &&
    hasMeaningfulVicidialValue(telephony.vicidialSession.phoneNumber) &&
    hasMeaningfulVicidialValue(telephony.vicidialSession.vendorLeadCode)
  const isInCall =
    !dispositionPending &&
    (
      vicidialStatus === 'INCALL' ||
      vicidialSubStatus.includes('INCALL') ||
      vicidialSubStatus.includes('LIVE')
    )
  const isIncomingAlert =
    !drawerState.visible &&
    telephony.vicidialSession.activeSession &&
    !isPausedState &&
    (hasIncomingLeadContext || hasCallContext) &&
    !isInCall

  const callerLabel = buildCallerLabel(
    telephony.vicidialSession.firstName,
    telephony.vicidialSession.lastName,
    telephony.vicidialSession.phoneNumber,
    telephony.vicidialSession.vendorLeadCode
  )

  const effectiveTooltipLabel = isIncomingAlert
    ? callerLabel
      ? `Incoming PolicyDial call: ${callerLabel}`
      : 'Incoming PolicyDial call'
    : isInCall
    ? callerLabel
      ? `PolicyDial call active: ${callerLabel}`
      : 'PolicyDial call active'
    : tooltipLabel

  const floatingButtonStyle: React.CSSProperties = {
    ...floatingTabStyle,
    ...(isIncomingAlert ? incomingPulseStyle : isInCall ? activeCallStyle : {}),
  }

  useEffect(() => {
    const handleDrawerState = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerStateDetail>
      if (!customEvent.detail) return
      setDrawerState(customEvent.detail)
    }

    window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handleDrawerState as EventListener)
    }
  }, [])

  const handleOpenVicidialWebphone = useCallback(async () => {
    if (drawerState.mounted && drawerState.provider === 'VICIDIAL') {
      window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_TOGGLE_EVENT))
      return
    }

    if (telephony.vicidialSession.activeSession) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    if (telephony.vicidialSession.connected || telephony.vicidialSession.connecting) {
      await telephony.openWebphone('VICIDIAL', {preferAgentSession: true})
      return
    }

    await telephony.connectVicidialSession()
  }, [drawerState.mounted, drawerState.provider, telephony])

  if (!telephony.config.vicidial_enabled) {
    return null
  }

  return (
    <div style={floatingToolsStyle} id="global-telephony-floating-tools">
      <style>
        {`
          @keyframes policytools-vicidial-pulse {
            0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); transform: translateX(0); }
            50% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0); transform: translateX(-2px); }
            100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); transform: translateX(0); }
          }
        `}
      </style>
      <OverlayTrigger
        placement='left'
        overlay={<Tooltip id='telephony-vicidial-webphone-tooltip'>{effectiveTooltipLabel}</Tooltip>}
      >
        <Button
          style={floatingButtonStyle}
          variant={
            isIncomingAlert || isInCall
              ? 'success'
              : drawerState.mounted && drawerState.visible
              ? 'dark'
              : telephony.vicidialSession.connected
              ? 'primary'
              : 'light'
          }
          title={effectiveTooltipLabel}
          onClick={handleOpenVicidialWebphone}
          disabled={!telephony.vicidialSession.connected && !telephony.vicidialSession.connecting}
        >
          <i className='bi bi-telephone fs-4' />
        </Button>
      </OverlayTrigger>
    </div>
  )
}
